import type { MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useHeadSlot, useLeaveWrite } from '../shell/headSlot';
import './table.css';

export interface BarWord {
  id: string;
  label: string;
  /** Its panel is open. */
  on: boolean;
  onPress: (event: ReactMouseEvent<HTMLButtonElement>) => void;
}

/** What Note does on a page: starts a sticky, pressed while its panel is open. */
export interface NoteWord {
  onPress?: () => void;
  /** Why Note does nothing, where it does not. */
  title?: string;
  pressed?: boolean;
}

/** Note, among the table's corner tools beside Browse. */
export function NoteButton({ note }: { note: NoteWord }) {
  return (
    <button type="button" className="table-note" aria-pressed={note.pressed} disabled={!note.onPress} title={note.title} onClick={note.onPress}>
      <PlusGlyph /> Note
    </button>
  );
}

/** Link, under Note and Browse: held on, every card on the canvas shows where a connection is
 *  drawn out of it. */
export function LinkButton({ on, onPress, title }: { on: boolean; onPress?: () => void; title?: string }) {
  return (
    <button type="button" className="table-link" aria-pressed={on} disabled={!onPress} title={title} onClick={onPress}>
      <LinkGlyph /> Link
    </button>
  );
}

function LinkGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.5 6h3a3 3 0 0 1 3 3v6a3 3 0 0 0 3 3h-2" />
    </svg>
  );
}

/**
 * The page's words, in the shell's head: the words for what the page holds after the places, and
 * [quiet], a line of state, at the far end. Drawn in place where there is no head, as a page on its
 * own is.
 */
export function PageBar({ words, quiet }: { words: BarWord[]; quiet?: string }) {
  const slot = useHeadSlot();
  useLeaveWrite();
  const bar = (
    <nav className="table-bar" aria-label="This page">
      {words.length > 0 && <span className="table-bar-rule" aria-hidden="true" />}
      <span className="table-bar-words">
        {words.map((word) => (
          <button key={word.id} type="button" className="table-word" aria-pressed={word.on} onClick={word.onPress}>
            {word.label}
          </button>
        ))}
      </span>
      <span className="table-bar-spacer" />
      {quiet && <span className="table-bar-quiet">{quiet}</span>}
    </nav>
  );
  return slot ? createPortal(bar, slot) : bar;
}

function PlusGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
